import { NoxusAlert } from "@/components/UI/NoxusAlert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { useAtomValue, useSetAtom } from "jotai";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { ExternalLink, Loader2, Plus, Route, Trash2 } from "lucide-react";
import { WorkflowService, WorkflowType } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { useRouter } from "next/navigation";
import { useRemoteData } from "@/utils/useRemoteData";
import { timeAgo } from "@/utils/generic";

import { hasQueueToSaveAtom, saveStateAtom } from "@/store/editor-jotai";
import { useWorkflowPickerSaveGate } from "./WorkflowPickerSaveGate";
import {
  getEmptyFlowDefinition,
  isEditorV2Enabled,
} from "@/utils/emptyFlowDefinition";

interface WorkflowPickerFieldProps {
  name: string;
  control: Control<any>;
  label: string;
  tooltip?: string;
  workflowId?: string | null;
  workflowName?: string | null;
  workflowIcon?: string | null;
  workflowType?: string;
  emptyLabel?: string;
  isDisabled?: boolean;
  defaultValue?: string;
}

export const WorkflowPickerField = ({
  name,
  control,
  label,
  tooltip,
  workflowId,
  workflowName,
  workflowIcon,
  workflowType = "task_automation",
  emptyLabel = "No automation flow",
  isDisabled = false,
  defaultValue,
}: WorkflowPickerFieldProps) => {
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const [isCreating, setIsCreating] = useState(false);
  const [isWaitingToNavigate, setIsWaitingToNavigate] = useState(false);
  const [isWaitingToDelete, setIsWaitingToDelete] = useState(false);
  const [localWorkflowName, setLocalWorkflowName] = useState<string | null>(
    null
  );
  // In the V2 editor a provider supplies the V2 save atoms; without it we read
  // the V1 editor atoms. Each editor writes save state to a different jotai
  // module, so reading the wrong one makes the wait-for-save gate a no-op.
  const gate = useWorkflowPickerSaveGate();
  const setHasQueueToSaveV1 = useSetAtom(hasQueueToSaveAtom);
  const saveStateV1 = useAtomValue(saveStateAtom);
  const saveState = gate?.saveState ?? saveStateV1;
  const queueSave = useCallback(() => {
    if (gate) gate.queueSave();
    else setHasQueueToSaveV1(true);
  }, [gate, setHasQueueToSaveV1]);

  const formValue = useWatch({ control, name });
  const resolvedWorkflowId = workflowId || formValue || null;
  const pendingNavigationPath = useRef<string | null>(null);
  // Epoch ms of when the user requested an action that must not happen
  // until the parent workflow has actually persisted the pending change.
  const actionRequestedAt = useRef<number | null>(null);

  useEffect(() => {
    if (actionRequestedAt.current == null) return;

    const requestedAt = actionRequestedAt.current;
    const savedAt = saveState.timestamp
      ? new Date(saveState.timestamp).getTime()
      : 0;

    // Only navigate once the parent workflow has genuinely persisted the
    // change carrying `automation_workflow_id`. `hasQueueToSave` is a
    // transient trigger the editor consumes/resets synchronously, and the
    // ~1s autosave debounce means "not saving" is briefly true long before
    // the PUT runs. Navigating in that window unmounts the editor and
    // cancels the debounced save, dropping the link. So wait for a real
    // completion: a `saved` state stamped after the request.
    if (saveState.status === "saved" && savedAt >= requestedAt) {
      actionRequestedAt.current = null;
      if (pendingNavigationPath.current) {
        const path = pendingNavigationPath.current;
        pendingNavigationPath.current = null;
        setIsWaitingToNavigate(false);
        router.push(path);
      }
      if (isWaitingToDelete) {
        setIsWaitingToDelete(false);
      }
    } else if (saveState.status === "errored") {
      // Save failed (saveWorkflowAtom already surfaced a toast). Abort the
      // pending action and re-enable the controls so the user can retry,
      // rather than navigating into a state where the link was lost.
      actionRequestedAt.current = null;
      pendingNavigationPath.current = null;
      setIsWaitingToNavigate(false);
      if (isWaitingToDelete) {
        setIsWaitingToDelete(false);
      }
    }
  }, [saveState.status, saveState.timestamp, router, isWaitingToDelete]);

  const { data: validity } = useRemoteData(
    "WORKFLOW_IS_VALID",
    { path: { group_id: spotGroup.id, workflow_id: resolvedWorkflowId ?? "" } },
    { enabled: !!resolvedWorkflowId }
  );

  const { data: workflowMeta } = useRemoteData(
    "WORKFLOWS_ID",
    { path: { group_id: spotGroup.id, workflow_id: resolvedWorkflowId ?? "" } },
    { enabled: !!resolvedWorkflowId }
  );
  const lastEditedAt = workflowMeta?.last_updated_at;

  const handleCreate = useCallback(
    async (onChange: (value: string) => void) => {
      setIsCreating(true);
      try {
        const result = await WorkflowService.addWorkflow({
          path: { group_id: spotGroup.id },
          body: {
            name: "Task Automation",
            type: workflowType as WorkflowType,
            definition: getEmptyFlowDefinition(
              isEditorV2Enabled(spotGroup.settings)
            ),
          },
        });
        const newId = result.data?.id;
        if (newId) {
          setLocalWorkflowName(result.data?.name || "Task Automation");
          onChange(newId);
          // Persist the new link on the parent workflow immediately so it
          // survives navigation and the backend can stamp the parent
          // back-reference. The Open button additionally waits for this
          // save to complete before navigating (see the effect above).
          queueSave();
        }
      } finally {
        setIsCreating(false);
      }
    },
    [spotGroup.id, spotGroup.settings, workflowType, queueSave]
  );

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue || ""}
      render={({ field: { onChange, value } }) => {
        const hasWorkflow = !!resolvedWorkflowId;
        const isTaskAutomation = workflowType === "task_automation";

        return (
          <div className="w-full">
            {label && !isTaskAutomation && (
              <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
            )}

            {hasWorkflow ? (
              <div className="space-y-3">
                {isTaskAutomation && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      Automation flow
                    </p>
                    <p className="text-xs text-muted-foreground">
                      An automation flow allows you to trigger a sequence of
                      actions automatically the moment this task is created.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                  <div
                    className={cn("flex min-w-0 items-center gap-2 text-sm")}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-50">
                      {isTaskAutomation ? (
                        <Route className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <NoxusIcon
                          icon={workflowIcon || "workflow"}
                          size="sm"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {workflowName || localWorkflowName || "Automation flow"}
                      </p>
                      {lastEditedAt && (
                        <p className="truncate text-xs text-muted-foreground">
                          Last edited {timeAgo(lastEditedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={isWaitingToNavigate}
                      onClick={() => {
                        if (!resolvedWorkflowId) return;
                        const path = `/flows/${resolvedWorkflowId}`;
                        // Fast path: nothing pending, so the link is
                        // already persisted — navigate right away instead
                        // of forcing a redundant save + 1s debounce wait.
                        if (
                          saveState.status === "saved" ||
                          saveState.status === "not_saved"
                        ) {
                          router.push(path);
                          return;
                        }
                        // Unsaved changes (e.g. just-created flow): persist
                        // first, then navigate once the save completes.
                        pendingNavigationPath.current = path;
                        actionRequestedAt.current = Date.now();
                        setIsWaitingToNavigate(true);
                        queueSave();
                      }}
                    >
                      {isWaitingToNavigate ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ExternalLink className="h-3.5 w-3.5" />
                      )}
                      {isWaitingToNavigate ? "Saving..." : "Open"}
                    </Button>
                    {!isDisabled && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={isWaitingToDelete}
                        onClick={() => {
                          setLocalWorkflowName(null);
                          onChange("");
                          actionRequestedAt.current = Date.now();
                          setIsWaitingToDelete(true);
                          queueSave();
                        }}
                      >
                        {isWaitingToDelete ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                {validity && !validity.is_valid && (
                  <NoxusAlert
                    look="banner"
                    title="This flow has configuration errors. Open the editor to resolve them."
                    variant="warning"
                    size="xs"
                  />
                )}
              </div>
            ) : (
              <div className="flex w-full flex-col items-center gap-6 py-6">
                <div className="flex w-full max-w-[308px] flex-col items-center gap-4 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-muted">
                    <Route className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-lg font-medium text-foreground">
                      {emptyLabel}
                    </p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      An automation flow allows you to trigger a sequence of
                      actions automatically the moment this task is created.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-2"
                  disabled={isDisabled || isCreating}
                  onClick={() => handleCreate(onChange)}
                >
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {isCreating ? "Creating..." : "Create automation flow"}
                </Button>
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
